import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Upload,
  FileText,
  Receipt,
  Building2,
  Banknote,
  Bell,
  Settings,
  LogOut,
  ClipboardCheck,
  Users,
  Trophy,
  TrendingUp,
  Sparkles,
  Mail,
  BookOpen,
  BarChart3,
  PanelLeftClose,
  PanelLeftOpen,
  Database,
  Activity,
} from 'lucide-react';

const SIDEBAR_STATE_KEY = 'mpl_sidebar_collapsed';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { user, logout, hasTabAccess, isReadOnly } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STATE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Fetch the default tournament so the header shows its name + logo
  const [defaultTournamentName, setDefaultTournamentName] = useState('Finance');
  const [defaultTournamentLogo, setDefaultTournamentLogo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tournaments`, { withCredentials: true });
        if (cancelled) return;
        const list = res?.data?.tournaments || [];
        const def = list.find((t) => t.is_default && t.is_active) || list.find((t) => t.is_default);
        if (def?.name) setDefaultTournamentName(def.name);
        setDefaultTournamentLogo(def?.logo_url || null);
      } catch {
        /* keep fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-fetch on route change so the header reflects updates made on Tournament Mgmt page
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // AI Assistant is pinned at the top and styled as the hero nav item.
  const aiItem = { name: 'AI Assistant', href: '/ai', icon: Sparkles, permission: 'ai' };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    { name: 'Budget Upload', href: '/budget-upload', icon: Upload, permission: 'budget_upload' },
    { name: 'Purchase Orders', href: '/purchase-orders', icon: FileText, permission: 'purchase_orders' },
    { name: 'Invoices', href: '/invoices', icon: Receipt, permission: 'invoices' },
    { name: 'Vendors', href: '/vendors', icon: Building2, permission: 'vendors' },
    { name: 'Bank', href: '/bank', icon: Banknote, permission: 'bank' },
    { name: 'Approvals', href: '/approvals', icon: ClipboardCheck, permission: 'approvals' },
    { name: 'Revenue', href: '/revenue', icon: TrendingUp, permission: 'revenue' },
    { name: 'Email Hub', href: '/email-hub', icon: Mail, permission: 'email_hub' },
    { name: 'Accounting', href: '/accounting', icon: BookOpen, permission: 'accounting' },
    { name: 'Reports', href: '/reports', icon: BarChart3, permission: 'reports' },
    { name: 'Alerts', href: '/alerts', icon: Bell, permission: 'alerts' },
    { name: 'Settings', href: '/settings', icon: Settings, permission: 'settings' },
  ];

  const filteredNavigation = navigation.filter((item) => hasTabAccess(item.permission));
  const showAi = hasTabAccess(aiItem.permission);

  const renderNavLink = (item, { isAi = false } = {}) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.href;
    const base = 'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors';
    const size = collapsed ? 'justify-center px-0 py-3' : 'px-4 py-3';

    if (isAi) {
      return (
        <Link
          key={item.name}
          to={item.href}
          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          title={collapsed ? item.name : undefined}
          className={`${base} ${size} relative overflow-hidden text-white shadow-sm ${
            isActive ? 'ring-2 ring-[#FF7F00]/60' : 'hover:brightness-110'
          }`}
          style={{
            background: 'linear-gradient(135deg, #00204F 0%, #0b3a7a 55%, #FF7F00 135%)',
          }}
        >
          <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.8} />
          {!collapsed && (
            <span className="flex-1 truncate">
              {item.name}
              <span className="ml-2 text-[9px] tracking-wider uppercase bg-white/20 px-1.5 py-0.5 rounded-sm align-middle">
                AI
              </span>
            </span>
          )}
        </Link>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
        title={collapsed ? item.name : undefined}
        className={`${base} ${size} ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
        {!collapsed && <span className="truncate">{item.name}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="w-full max-w-screen-2xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button
              data-testid="sidebar-toggle-button"
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </Button>
            <img
              src={defaultTournamentLogo || '/brand/gdca-logo.jpg'}
              alt={defaultTournamentName || 'GDCA'}
              className="h-9 w-9 object-contain rounded-sm bg-white border border-border"
              data-testid="app-header-tournament-logo"
            />
            <h1
              className="text-lg sm:text-xl font-heading font-bold tracking-tight text-primary leading-tight truncate max-w-[40vw] sm:max-w-none"
              data-testid="app-header-tournament-name"
              title={defaultTournamentName}
            >
              {defaultTournamentName}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{user?.name}</span>
              <span className="ml-2 text-xs uppercase bg-muted px-2 py-1 rounded">
                {user?.is_super_admin ? 'Super Admin' : user?.role}
              </span>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Read-only viewer banner — only visible to viewers. Sets expectation
          that edits/creates will not be possible regardless of which tab is
          open. Backend middleware enforces the actual restriction. */}
      {isReadOnly && (
        <div
          className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-900 flex items-center justify-center gap-2"
          data-testid="read-only-banner"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
          Read-only viewer — you can browse but cannot create, edit, or delete records.
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            collapsed ? 'w-16' : 'w-64'
          } transition-[width] duration-200 border-r bg-white h-[calc(100vh-4rem)] sticky top-16 flex-shrink-0 overflow-y-auto`}
          data-testid="app-sidebar"
          data-collapsed={collapsed ? 'true' : 'false'}
        >
          <nav className={`${collapsed ? 'p-2' : 'p-4'} space-y-1`}>
            {/* AI Assistant pinned on top */}
            {showAi && (
              <>
                {renderNavLink(aiItem, { isAi: true })}
                <div className="border-t my-2" />
              </>
            )}

            {filteredNavigation.map((item) => renderNavLink(item))}

            {/* User Management - Super Admin Only */}
            {user?.is_super_admin && (
              <>
                <div className="border-t my-2" />
                {renderNavLink({
                  name: 'User Management',
                  href: '/users',
                  icon: Users,
                  permission: '__super',
                })}
                {renderNavLink({
                  name: 'Tournaments',
                  href: '/tournaments',
                  icon: Trophy,
                  permission: '__super',
                })}
                {renderNavLink({
                  name: 'Divisions',
                  href: '/divisions',
                  icon: Building2,
                  permission: '__super',
                })}
                {renderNavLink({
                  name: 'DB Cleanup',
                  href: '/admin/cleanup',
                  icon: Database,
                  permission: '__super',
                })}
                {renderNavLink({
                  name: 'Live Monitor',
                  href: '/admin/monitor',
                  icon: Activity,
                  permission: '__super',
                })}
              </>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 xl:p-10">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
